import { type ComponentProps, useMemo, useState } from 'react';
import { InfinityScrollSelect } from 'ui';
import { useDebounce } from 'common/hooks';
import { useChatUsersInfiniteQuery } from '../../queries';
import { type ChatCreateUserOption, useChatCreateUserSelectCreateOption } from './hooks';

type P = Omit<
  ComponentProps<typeof InfinityScrollSelect<ChatCreateUserOption>>,
  'handleRequestMoreData' | 'hasMoreData' | 'options'
>;

const ChatCreateUserSelect = ({ defaultOpen, value: selectedValue, ...selectProperties }: P) => {
  const [search, setSearch] = useState<string>();
  const { isDebouncing, value } = useDebounce(search);
  const createOption = useChatCreateUserSelectCreateOption();

  const {
    data: users,
    fetchNextPage: fetchNextPageOfUsers,
    hasNextPage: hasMoreUsers,
    isFetching: isFetchingUsers,
  } = useChatUsersInfiniteQuery({
    filter: { search: value },
  });

  const options = useMemo(() => {
    // TODO: find out why selected option from initial value must be added to the options list
    const loadedOptions = users?.pages.flatMap((page) => page.map((user) => createOption(user))) ?? [];
    const selectedOptions: ChatCreateUserOption[] = Array.isArray(selectedValue) ? selectedValue : [];
    const loadedOptionValues = new Set(loadedOptions.map((option) => option.value));
    const selectedOptionsMissingFromLoaded = selectedOptions.filter((option) => !loadedOptionValues.has(option.value));

    return [...selectedOptionsMissingFromLoaded, ...loadedOptions];
  }, [createOption, selectedValue, users]);

  return (
    <InfinityScrollSelect<ChatCreateUserOption>
      // eslint-disable-next-line jsx-a11y/no-autofocus -- intentionally autofocus the select when new chat is creating
      autoFocus={defaultOpen}
      defaultOpen={defaultOpen}
      fieldNames={{ label: 'optionLabel' }}
      fullWidth
      handleRequestMoreData={fetchNextPageOfUsers}
      hasMoreData={hasMoreUsers}
      loading={isFetchingUsers || isDebouncing}
      mode="multiple"
      optionLabelProp="label"
      options={options}
      showSearch={{ filterOption: false, onSearch: setSearch }}
      {...selectProperties}
      value={selectedValue}
    />
  );
};

export default ChatCreateUserSelect;
